/**
 * AGREEMENT ACCEPTANCE MODAL
 *
 * Modal component for displaying and accepting legal agreements.
 * Shows when users have pending agreements that need acceptance.
 *
 * USAGE:
 *   <AgreementAcceptanceModal
 *     isOpen={hasPendingAgreements}
 *     agreements={pendingAgreements}
 *     onAccept={handleAccept}
 *     onClose={handleClose}
 *     businessId={businessId} // Optional, for seller agreements
 *   />
 *
 * CREATED: July 2025
 */

import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { acceptMultipleAgreements } from '../../functions/agreement';
import './AgreementAcceptanceModal.scss';

const AgreementAcceptanceModal = ({
  isOpen,
  agreements = [],
  onAccept,
  onClose,
  businessId = null,
  allowClose = false, // If false, user must accept to proceed
  title = 'Agreement Updates'
}) => {
  const [selectedAgreements, setSelectedAgreements] = useState({});
  const [expandedAgreement, setExpandedAgreement] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState({});

  const { data: user } = useSelector((state) => state.user);

  if (!isOpen || agreements.length === 0) return null;

  const allSelected = agreements.every(a => selectedAgreements[a._id]);

  const handleCheckboxChange = (agreementId) => {
    // Only allow checking if scrolled to bottom
    if (!scrolledToBottom[agreementId]) {
      toast.info('Please read the entire agreement before accepting');
      // Auto-expand to help user
      setExpandedAgreement(agreementId);
      return;
    }
    setSelectedAgreements(prev => ({
      ...prev,
      [agreementId]: !prev[agreementId]
    }));
  };

  const handleScroll = (e, agreementId) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Consider scrolled to bottom if within 50px of the end
    if (scrollHeight - scrollTop - clientHeight < 50) {
      setScrolledToBottom(prev => ({
        ...prev,
        [agreementId]: true
      }));
    }
  };

  const handleAcceptAll = async () => {
    if (!allSelected) {
      toast.error('Please accept all agreements to continue');
      return;
    }

    setSubmitting(true);
    try {
      const agreementIds = agreements.map(a => a._id);
      await acceptMultipleAgreements(agreementIds, user.token, businessId);

      toast.success('Agreements accepted successfully');

      if (onAccept) {
        onAccept();
      }
    } catch (error) {
      console.error('Error accepting agreements:', error);
      toast.error(error.response?.data?.error || 'Failed to accept agreements');
    } finally {
      setSubmitting(false);
    }
  };

  const formatContent = (content, format) => {
    if (format === 'markdown') {
      // Basic markdown rendering - for production, use a proper markdown library
      return content;
    }
    return content;
  };

  const getAgreementTypeLabel = (type) => {
    const labels = {
      terms_of_service: 'Terms of Service',
      privacy_policy: 'Privacy Policy',
      seller_agreement: 'Seller Agreement',
      buyer_agreement: 'Buyer Agreement'
    };
    return labels[type] || type;
  };

  return (
    <div className="agreement-modal-overlay">
      <div className="agreement-modal">
        <div className="agreement-modal__header">
          <h2 className="agreement-modal__title">{title}</h2>
          {allowClose && (
            <button
              className="agreement-modal__close"
              onClick={onClose}
              aria-label="Close"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        <div className="agreement-modal__intro">
          <p>
            {agreements.length === 1
              ? 'Please review and accept the following agreement to continue:'
              : `Please review and accept the following ${agreements.length} agreements to continue:`}
          </p>
        </div>

        <div className="agreement-modal__content">
          {agreements.map((agreement) => (
            <div
              key={agreement._id}
              className={`agreement-modal__agreement ${
                expandedAgreement === agreement._id ? 'agreement-modal__agreement--expanded' : ''
              }`}
            >
              <div
                className="agreement-modal__agreement-header"
                onClick={() => setExpandedAgreement(
                  expandedAgreement === agreement._id ? null : agreement._id
                )}
              >
                <div className="agreement-modal__agreement-info">
                  <span className="agreement-modal__agreement-type">
                    {getAgreementTypeLabel(agreement.type)}
                  </span>
                  <h3 className="agreement-modal__agreement-title">
                    {agreement.title}
                  </h3>
                  <span className="agreement-modal__agreement-version">
                    Version {agreement.version}
                  </span>
                </div>
                <div className="agreement-modal__expand-action">
                  {expandedAgreement !== agreement._id && (
                    <span className="agreement-modal__tap-hint">
                      Tap to read
                    </span>
                  )}
                  <svg
                    className={`agreement-modal__expand-icon ${
                      expandedAgreement === agreement._id ? 'agreement-modal__expand-icon--expanded' : ''
                    }`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {expandedAgreement === agreement._id && (
                <div
                  className="agreement-modal__agreement-content"
                  onScroll={(e) => handleScroll(e, agreement._id)}
                >
                  {agreement.changesSummary && (
                    <div className="agreement-modal__changes-summary">
                      <strong>What's changed:</strong> {agreement.changesSummary}
                    </div>
                  )}
                  <div
                    className="agreement-modal__agreement-text"
                    dangerouslySetInnerHTML={{
                      __html: formatContent(agreement.content, agreement.contentFormat)
                    }}
                  />
                  {!scrolledToBottom[agreement._id] && (
                    <div className="agreement-modal__scroll-hint">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                      </svg>
                      Scroll down to read the entire agreement
                    </div>
                  )}
                </div>
              )}

              <div className="agreement-modal__checkbox-section">
                {!scrolledToBottom[agreement._id] && expandedAgreement !== agreement._id && (
                  <div className="agreement-modal__read-first-hint">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 16v-4M12 8h.01" />
                    </svg>
                    Please tap above to read the agreement first
                  </div>
                )}
                <label
                  className={`agreement-modal__checkbox-wrapper ${
                    !scrolledToBottom[agreement._id] ? 'agreement-modal__checkbox-wrapper--disabled' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedAgreements[agreement._id] || false}
                    onChange={() => handleCheckboxChange(agreement._id)}
                    disabled={!scrolledToBottom[agreement._id]}
                  />
                  <span className="agreement-modal__checkbox-label">
                    I have read and agree to the {agreement.title}
                  </span>
                </label>
              </div>
            </div>
          ))}
        </div>

        <div className="agreement-modal__footer">
          <p className="agreement-modal__footer-note">
            By accepting, you agree to be bound by these terms.
          </p>
          <div className="agreement-modal__footer-buttons">
            <button
              className="agreement-modal__accept-btn"
              onClick={handleAcceptAll}
              disabled={!allSelected || submitting}
            >
              {submitting ? (
                <>
                  <svg className="agreement-modal__spinner" viewBox="0 0 24 24">
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeDasharray="45 45"
                    />
                  </svg>
                  Accepting...
                </>
              ) : (
                <>Accept {agreements.length > 1 ? 'All' : ''} & Continue</>
              )}
            </button>
            {allowClose && (
              <button
                className="agreement-modal__cancel-btn"
                onClick={onClose}
                disabled={submitting}
              >
                Maybe Later
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgreementAcceptanceModal;
